<template>
  <DashboardLayout :breadcrumbs="breadcrumbs">
    <div class="container is-fluid">
      <div class="level">
        <div class="level-left">
          <h1 class="title is-5">{{ $t('documents.list') }}</h1>
        </div>
        <div class="level-right">
          <div class="field is-grouped controls">
            <div class="control">
              <button @click="goTo('createDocument')"
                      class="button is-success">
                {{ $t('documents.newDocument') }}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div class="level table-actions">
        <div class="level-left">
          <form @submit.prevent="onSearch">
            <div class="field is-grouped controls">
              <div class="control">
                <b-input v-model="filters.q" :placeholder="$t('interface.search')"></b-input>
              </div>
              <div class="control mr-10">
                <button type="submit" class="button is-primary">
                  <b-icon icon="magnify" size="is-small"></b-icon>
                </button>
              </div>
            </div>
          </form>
        </div>
        <div class="level-right">
          <div class="control mt-5">
            <a @click="showFilters = !showFilters">
              <b-icon icon="filter-outline" size="is-small"></b-icon>
              {{ $t('interface.advancedSearch') }}
              <b-icon icon="chevron-down" size="is-small"></b-icon>
            </a>
          </div>
        </div>
      </div>

      <div v-if="showFilters" class="mt-15">
        <form @submit.prevent="onFilter">
          <div class="columns is-multiline">
            <div class="column is-2">
              <b-field>
                <form-select :label="$t('documents.filters.role')" v-model="filters.list_mode">
                  <option value="all">{{ $t('interface.all') }}</option>
                  <option value="authored">{{ $t('interface.authored') }}</option>
                  <option value="maintained">{{ $t('interface.maintained') }}</option>
                  <option value="executed">{{ $t('interface.executed') }}</option>
                </form-select>
              </b-field>
            </div>
            <div class="column is-2">
              <b-field>
                <form-select :label="$t('documents.filters.expiration')" v-model="filters.expiration">
                  <option value="all">{{ $t('interface.all') }}</option>
                  <option value="expired">{{ $t('interface.expired') }}</option>
                  <option value="expiring">{{ $t('interface.expiring') }}</option>
                  <option value="without_term">{{ $t('interface.without_term') }}</option>
                </form-select>
              </b-field>
            </div>
            <div class="column is-2">
              <b-field>
                <form-select :label="$t('documents.filters.state')" v-model="filters.state">
                  <option value="all">{{ $t('interface.all') }}</option>
                  <option value="active">{{ $t('workflow.states.active') }}</option>
                  <option value="draft">{{ $t('workflow.states.draft') }}</option>
                  <option value="new">{{ $t('workflow.states.new') }}</option>
                  <option value="in_process">{{ $t('workflow.states.in_process') }}</option>
                  <option value="cancelled">{{ $t('workflow.states.cancelled') }}</option>
                  <option value="done">{{ $t('workflow.states.done') }}</option>
                  <option value="rejected">{{ $t('workflow.states.rejected') }}</option>
                  <option value="closed">{{ $t('workflow.states.closed') }}</option>
                </form-select>
              </b-field>
            </div>
            <div class="column is-2">
              <b-field>
                <form-select :label="$t('documents.filters.priority')" v-model="filters.priority">
                  <option value="none">{{ $t('interface.all') }}</option>
                  <option value="low">{{ $t('workflow.priorities.low') }}</option>
                  <option value="medium">{{ $t('workflow.priorities.medium') }}</option>
                  <option value="high">{{ $t('workflow.priorities.high') }}</option>
                </form-select>
              </b-field>
            </div>
            <div class="column is-2">
              <b-field>
                <form-select :label="$t('documents.filters.type')" v-model="filters.type">
                  <option value="none">{{ $t('interface.all') }}</option>
                  <option value="info">{{ $t('workflow.doctypes.info') }}</option>
                  <option value="task">{{ $t('workflow.doctypes.task') }}</option>
                </form-select>
              </b-field>
            </div>
            <div class="column is-2">
              <b-field>
                <form-select :label="$t('documents.fields.sourceType')" v-model="filters.source_type">
                  <option value="none">{{ $t('interface.all') }}</option>
                  <option value="standalone">{{ $t('documents.sourceTypes.standalone') }}</option>
                  <option value="document">{{ $t('documents.sourceTypes.document') }}</option>
                  <option value="document_resolution">{{ $t('documents.sourceTypes.document_resolution') }}</option>
                  <option value="document_plan">{{ $t('documents.sourceTypes.document_plan') }}</option>
                  <option value="project">{{ $t('documents.sourceTypes.project') }}</option>
                  <option value="program">{{ $t('documents.sourceTypes.program') }}</option>
                </form-select>
              </b-field>
            </div>
            <div class="column is-12">
              <b-checkbox class="mr-15" v-model="filters.based_on_docs">{{ $t('documents.fields.based_on_docs') }}</b-checkbox>
            </div>
            <div class="column is-12">
              <div class="field is-grouped">
                <div class="control">
                  <button type="submit" class="button is-primary">{{ $t('interface.apply') }}</button>
                </div>
                <div class="control">
                  <button type="button" class="button is-danger" @click="resetFilters">
                    <b-icon icon="filter-remove-outline" size="is-small"></b-icon>
                    <span>{{ $t('interface.clearFilters') }}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>

      <b-table class="mt-10 disable-checkable-all"
               :data="data"
               :loading="loading"

               paginated
               backend-pagination
               :total="total"
               :per-page="perPage"
               :current-page.sync="page"
               @page-change="onPageChange"

               backend-sorting
               default-sort-direction="desc"
               :default-sort="[sortField, sortOrder]"
               @sort="onSort">
        <template slot-scope="props">
          <b-table-column width="30">
            <button class="check"
                    v-if="!props.row.is_auto_generated && props.row.is_author && props.row.state === 'done'"
                    @click="acceptAssignment(props.row.id)">
              <b-icon icon="check-bold" size="is-small"></b-icon>
            </button>
          </b-table-column>
          <b-table-column :label="$t('documents.fields.title')" field="name" sortable>
            <a @click.prevent="detail(props.row.id)" style="position: relative">
              <span class="unread-indicator" v-if="!props.row.is_viewed"></span>
              <span :class="`priority ${props.row.priority} mr-10`"></span>
              <span>{{ props.row.name }}</span>
              <b-tooltip :label="$t('documents.infoDoc')"
                         type="is-light">
                <b-icon v-if="props.row.type === 'info'"
                        icon="information-outline"
                        size="is-small"
                        class="has-text-grey-light mr-0 ml-5">
                </b-icon>
              </b-tooltip>
            </a>
          </b-table-column>
          <b-table-column :label="$t('documents.fields.state')" width="100" field="state" sortable>
            {{ $t(`workflow.states.${props.row.state}`) }}
          </b-table-column>
          <b-table-column :label="$t('documents.fields.maintainer')" width="180">
            <b-tooltip :label="$t('documents.notViewedByMaintainer')"
                       v-if="!props.row.is_viewed_by_maintainer"
                       type="is-light">
              <b-icon icon="eye-off-outline"
                      size="is-small"
                      class="has-text-grey-light mr-0 ml-5">
              </b-icon>
            </b-tooltip>
            <b-tooltip :label="$t('documents.viewedByMaintainer')"
                       v-else
                       type="is-light">
              <b-icon icon="eye-outline"
                      size="is-small"
                      class="has-text-grey-light mr-0 ml-5">
              </b-icon>
            </b-tooltip>
            {{ props.row.maintainer__initials }}
          </b-table-column>
          <b-table-column :label="$t('documents.fields.author')" width="160">
            {{ props.row.author__initials }}
          </b-table-column>
          <b-table-column :label="$t('documents.fields.term')" width="150" field="term_date" sortable>
            <template v-if="props.row.term_date">
              <span :class="{expired: props.row.is_expired}">
                {{ props.row.term_date | localedDateWithYear }}
              </span>
            </template>
          </b-table-column>
          <b-table-column :label="$t('documents.fields.sourceType')" width="150" field="source_type" sortable>
            <template>
              {{ $t(`documents.sourceTypes.${props.row.source_type}`) }}
            </template>
          </b-table-column>
          <b-table-column :label="$t('documents.fields.tasks')" width="100">
            <b-tooltip
                :label="`${props.row.tasks_count} ${$t('documents.taskDetail.total')}, ${props.row.completed_tasks_count} ${$t('documents.taskDetail.done')}, ${props.row.expired_tasks_count} ${$t('documents.taskDetail.expired')}`"
                dashed type="is-light">
              {{ `${props.row.tasks_count}/${props.row.completed_tasks_count}/${props.row.expired_tasks_count}` }}
            </b-tooltip>
          </b-table-column>
        </template>

        <template slot="bottom-left">
          <select class="paginationPager"
                  v-model="page"
                  @change="() => onPageChange(page)">
            <option v-for="page in pages" :value="page">{{ page }}</option>
          </select>
        </template>

        <template slot="empty">
          <section class="section">
            <div class="content is-fluid has-text-grey has-text-centered">
              <p>{{ $t('interface.nothingFound') }}
                <a class="is-dashed" @click.prevent="resetFilters">{{ $t('interface.resetFiltersAndSearch') }}</a>.
              </p>
            </div>
          </section>
        </template>
      </b-table>
    </div>
  </DashboardLayout>
</template>

<script>
  import {mapGetters, mapState} from 'vuex'
  import {priorityColors, stateColors} from '@/colorTypes'
  import DashboardLayout from '@/components/DashboardLayout'
  import GovernmentAgencySelect from '@/components/GovernmentAgencySelect'

  export default {
    components: {
      DashboardLayout,
      GovernmentAgencySelect
    },
    data() {
      return {
        data: [],
        loading: false,
        total: 0,
        page: 1,
        perPage: 15,
        sortField: '',
        sortOrder: 'desc',
        priorityColors,
        stateColors,
        showFilters: false,
        filters: {}
      }
    },
    methods: {
      loadData() {
        this.loading = true
        this.checkedRows = []
        this.$axios.get('workflow/documents/list/', {
          params: {
            page: this.page,
            page_size: this.perPage,
            q: this.q,
            ordering: `${this.sortOrder === 'desc' ? '-' : ''}${this.sortField}`,
            ...this.prepareFilter('assignmentFilters')
          }
        }).then((response) => {
          this.loading = false
          this.total = response.data.count
          this.data = response.data.results
        }).catch((error) => {
          console.warn('ERROR:', error)
        })
      },
      onSort(field, order) {
        this.sortField = field
        this.sortOrder = order
        this.loadData()
      },
      onPageChange(page) {
        this.page = page
        this.loadData()
      },
      onSearch() {
        this.$store.commit('Filters/SET', ['assignmentFilters', this.filters]);
        this.loadData()
      },
      onFilter() {
        this.$store.commit('Filters/SET', ['assignmentFilters', this.filters]);
        this.loadData()
      },
      resetFilters() {
        this.$store.commit('Filters/RESET', 'assignmentFilters');
        this.filters = {...this.assignmentFilters};
        this.showFilters = false
        this.loadData()
      },
      goTo(route) {
        this.$router.push({name: route})
      },
      detail(id) {
        this.$router.push({
          name: 'documentDetail',
          params: {documentId: id}
        })
      },
      acceptAssignment(id) {
        this.$store.dispatch('Document/nextStateById', {id, note: null}).then(this.loadData)
      }
    },
    mounted() {
      this.filters = {...this.assignmentFilters}
      this.loadData()
      this.$store.dispatch('Document/getRelations')
    },
    computed: {
      ...mapState('Document', ['projects', 'branches', 'docTypes', 'registerBooks']),
      ...mapGetters('Account', ['isWorkflowController', 'account']),
      ...mapState('Filters', ['assignmentFilters']),
      ...mapGetters('Filters', ['prepareFilter']),
      checkedRowsCount() {
        return this.checkedRows.length
      },
      breadcrumbs() {
        return [
          {name: 'documents'}
        ]
      },
      pages() {
        let quotient = Math.floor(this.total / this.perPage)
        let reminder = this.total % this.perPage;
        return reminder === 0 ? quotient : quotient + 1;
      },
    }
  }
</script>

<style lang="scss" scoped>
  button.check {
    vertical-align: top;
    background: #3498db;
    border: 0;
    width: 20px;
    height: 20px;
    line-height: 12px;
    text-align: center;
    padding: 0;
    border-radius: 10px;
    cursor: pointer;

    &:hover {
      background: #00b2ff;
    }

    .icon {
      color: #fff;
      font-size: 12px;
    }
  }
</style>
